import {
    BarsOutlined,
    CalculatorOutlined,
    CloseOutlined,
    CodeOutlined,
    CopyOutlined,
    FormOutlined,
    FunctionOutlined,
    InfoCircleOutlined,
    NodeIndexOutlined,
    SearchOutlined,
    SettingOutlined,
    StarOutlined,
} from "@ant-design/icons";
import { Input, Popover, Typography } from "antd";
import { getNodeEntry } from "components/CC/Nodes/node-config";
import { NodeType } from "components/CC/types/enums";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { ReactNode, useEffect, useState } from "react";
import { toast } from "react-toastify";
import theme from "theme.json";

// Tab types for filtering
type TabKey =
    | "favorites"
    | "form_fields"
    | "keys"
    | "rows"
    | "system"
    | "steps"
    | "formulas"
    | "functions"
    | "format_convert"
    | "math_functions"
    | "operators";

// Color constants for parameter types - using theme.json colors
export const PARAMETER_COLORS = {
    form_fields: theme.token["blue.6"],
    keys: theme.token["green.6"],
    rows: theme.token["orange.6"],
    system: theme.token["purple.6"],
    /** Set step output variables — blue, matches Set node color */
    steps: "#2F54EB",
    /** HTTP response values (parsedBody.*, query.*, headers.*, status) — teal, matches HTTP node color */
    http_steps: theme.token["cyan.6"],
    /** Commission formula fields (commission/value/parent/price) */
    formulas: theme.token["gold.6"],
    format_convert: theme.token["gold.6"],
    math_functions: theme.token["cyan.6"],
    operators: theme.token["magenta.6"],
} as const;

// Maps a paramType/category type to its badge color — used for both item pills and category defaults
const PARAM_TYPE_COLOR_MAP: Partial<Record<NonNullable<ParameterItem["paramType"]> | TabKey, string>> = {
    form_fields: PARAMETER_COLORS.form_fields,
    keys: PARAMETER_COLORS.keys,
    rows: PARAMETER_COLORS.rows,
    system: PARAMETER_COLORS.system,
    steps: PARAMETER_COLORS.steps,
    http_steps: PARAMETER_COLORS.http_steps,
    formulas: PARAMETER_COLORS.formulas,
    format_convert: PARAMETER_COLORS.format_convert,
    math_functions: PARAMETER_COLORS.math_functions,
    operators: PARAMETER_COLORS.operators,
};

// Tab configuration
const PARAMETER_TABS: { key: TabKey; icon: typeof StarOutlined; labelKey: string }[] = [
    { key: "favorites", icon: StarOutlined, labelKey: "parameter_picker.categories.favorites" },
    { key: "form_fields", icon: FormOutlined, labelKey: "parameter_picker.categories.form_fields" },
    { key: "keys", icon: CodeOutlined, labelKey: "parameter_picker.categories.keys" },
    { key: "system", icon: SettingOutlined, labelKey: "parameter_picker.categories.system" },
    { key: "steps", icon: NodeIndexOutlined, labelKey: "parameter_picker.categories.steps" },
    { key: "rows", icon: BarsOutlined, labelKey: "parameter_picker.categories.rows" },
    { key: "formulas", icon: CalculatorOutlined, labelKey: "parameter_picker.categories.formulas" },
    { key: "functions", icon: FunctionOutlined, labelKey: "parameter_picker.categories.functions" },
];

// ============================================================================
// Parameter Item Types
// ============================================================================

export interface ParameterItem {
    id: string;
    name: string;
    /** @deprecated Use paramType instead */
    color?: string;
    /** Type of parameter - determines badge color */
    paramType?:
        | "form_fields"
        | "keys"
        | "rows"
        | "system"
        | "steps"
        | "http_steps"
        | "formulas"
        | "format_convert"
        | "math_functions"
        | "operators";
    /** If provided, used as the select value instead of wrapping id in {} */
    value?: string;
    /** If provided, shown in the tag pill instead of the raw inner value */
    displayLabel?: string;
    children?: ParameterItem[];
    /** NodeType string (e.g. NodeType.Set) — used to render the node's icon in the picker and tag pill */
    nodeType?: string;
    /**
     * When true, selecting this item inserts "{id}" as plain editable text with the caret placed
     * before the closing brace, instead of an atomic tag pill.
     */
    insertAsRawText?: boolean;
    /**
     * When true, any tag whose inner value starts with this item's `id` is treated as valid and
     * colored with this item's paramType — not just an exact match.
     */
    matchByPrefix?: boolean;
    /**
     * If true, selecting this item inserts raw, uncommitted tag text ending in a trailing "."
     * (e.g. "{step.parsedBody.}") so the user can continue typing a nested path.
     */
    insertAsRawContainer?: boolean;
}

export interface ParameterCategory {
    id: string;
    name: string;
    icon?: ReactNode;
    type: TabKey;
    count?: number;
    description?: string;
    items: ParameterItem[];
    /** When true, this category starts expanded when the picker opens, on every tab it appears in. */
    defaultExpanded?: boolean;
}

// ============================================================================
// Source Option Type
// ============================================================================
// Main Popover Component
// ============================================================================

interface ParameterPickerProps {
    categories: ParameterCategory[];
    onSelect: (value: string, item: ParameterItem) => void;
    children: ReactNode;
    disabled?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    trigger?: ("hover" | "focus" | "click" | "contextMenu")[];
    /**
     * Controlled search value for the picker's search field — e.g. mirrored live from text typed
     * inside a "{...}" span in a TagTextInput, so the user doesn't have to click into the search
     * field separately. Falls back to internal state when omitted.
     */
    search?: string;
    onSearchChange?: (search: string) => void;
}

export const ParameterPicker = ({
    categories,
    onSelect,
    children,
    disabled = false,
    open: controlledOpen,
    onOpenChange,
    trigger = ["click"],
    search: controlledSearch,
    onSearchChange,
}: ParameterPickerProps) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const open = controlledOpen ?? uncontrolledOpen;

    const [uncontrolledSearch, setUncontrolledSearch] = useState("");
    const search = controlledSearch ?? uncontrolledSearch;

    const handleOpenChange = (nextOpen: boolean) => {
        if (controlledOpen === undefined) {
            setUncontrolledOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
    };

    const handleSearchChange = (nextSearch: string) => {
        if (controlledSearch === undefined) {
            setUncontrolledSearch(nextSearch);
        }
        onSearchChange?.(nextSearch);
    };

    const handleSelect = (value: string, item: ParameterItem) => {
        onSelect(value, item);
        // Don't close - allow multiple selections
    };

    const handleClose = () => handleOpenChange(false);

    if (disabled) {
        return <>{children}</>;
    }

    return (
        <Popover
            open={open}
            onOpenChange={handleOpenChange}
            placement="left"
            trigger={trigger}
            classNames={{
                body: "!p-0 rounded",
                root: "rounded",
            }}
            content={
                <ParameterPickerMenu
                    categories={categories}
                    onSelect={handleSelect}
                    onClose={handleClose}
                    search={search}
                    onSearchChange={handleSearchChange}
                />
            }
        >
            {children}
        </Popover>
    );
};

// ============================================================================
// Menu Component
// ============================================================================

interface ParameterPickerMenuProps {
    categories: ParameterCategory[];
    onSelect: (value: string, item: ParameterItem) => void;
    onClose: () => void;
    search: string;
    onSearchChange: (search: string) => void;
}

const ParameterPickerMenu = ({ categories, onSelect, onClose, search, onSearchChange }: ParameterPickerMenuProps) => {
    const { t } = usePaldockTranslation("components.connection-creator");
    const [activeTab, setActiveTab] = useState<TabKey>("favorites");
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

    const toggleExpanded = (id: string) => {
        setExpandedItems((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    // Only show content tabs when categories of that type exist.
    const availableTypes = new Set(categories.map((cat) => cat.type));
    const visibleTabs = PARAMETER_TABS.filter((tab) => {
        if (tab.key === "favorites") return true;
        if (tab.key === "functions") {
            return (
                availableTypes.has("format_convert") ||
                availableTypes.has("math_functions") ||
                availableTypes.has("operators")
            );
        }
        return availableTypes.has(tab.key);
    });

    // Reset to favorites if active tab is no longer visible
    useEffect(() => {
        if (!visibleTabs.some((tab) => tab.key === activeTab)) {
            setActiveTab("favorites");
        }
    }, [activeTab, visibleTabs]);

    // Seed expandedItems when switching to a non-favorites tab so categories start open, but let
    // subsequent header clicks collapse/expand freely. Categories marked `defaultExpanded` start
    // open on every tab, including favorites.
    useEffect(() => {
        const alwaysExpanded = categories.filter((c) => c.defaultExpanded).map((c) => c.id);
        if (activeTab === "favorites" || activeTab === "functions") {
            setExpandedItems(new Set(alwaysExpanded));
        } else {
            setExpandedItems(
                new Set([...categories.filter((c) => c.type === activeTab).map((c) => c.id), ...alwaysExpanded])
            );
        }
    }, [activeTab, categories]);

    // Filter categories based on active tab
    const filteredCategories = categories.filter((cat) => {
        if (activeTab === "favorites") {
            return (
                cat.type === "form_fields" ||
                cat.type === "keys" ||
                cat.type === "rows" ||
                cat.type === "system" ||
                cat.type === "steps" ||
                cat.type === "formulas"
            );
        }
        if (activeTab === "functions") {
            return cat.type === "format_convert" || cat.type === "math_functions" || cat.type === "operators";
        }
        return cat.type === activeTab;
    });

    const helpContent = (
        <div style={{ maxWidth: 280 }} className="text-sm text-gray-700 space-y-1.5">
            <p className="font-semibold text-gray-900">{t("parameter_picker.help.title")}</p>
            <p>{t("parameter_picker.help.intro")}</p>
            <ul className="space-y-1 pl-2 mb-1.5">
                <li>
                    <code className="bg-gray-100 px-1 rounded">{"{param}"}</code> —{" "}
                    {t("parameter_picker.help.constant")}
                </li>
                <li>
                    <code className="bg-gray-100 px-1 rounded">{"{data_param}"}</code> —{" "}
                    {t("parameter_picker.help.form_field")}
                </li>
                <li>
                    <code className="bg-gray-100 px-1 rounded">{"{<step>.parsedBody.user.id}"}</code> —{" "}
                    {t("parameter_picker.help.body")}
                </li>
                <li>
                    <code className="bg-gray-100 px-1 rounded">{"{<step>.query.ref}"}</code> —{" "}
                    {t("parameter_picker.help.query")}
                </li>
                <li>
                    <code className="bg-gray-100 px-1 rounded">{"{<step>.headers.content-type}"}</code> —{" "}
                    {t("parameter_picker.help.headers")}
                </li>
                <li>
                    <code className="bg-gray-100 px-1 rounded">{"{<step>.status}"}</code> —{" "}
                    {t("parameter_picker.help.status")}
                </li>
            </ul>
            <p className="text-gray-500 italic">{t("parameter_picker.help.color_hint")}</p>
        </div>
    );

    return (
        <div className="flex flex-col" style={{ width: 320 }}>
            {/* Header with tabs */}
            <div className="flex items-center justify-between bg-primary px-2 py-2">
                <div className="flex gap-1">
                    {visibleTabs.map((tab) => {
                        const Icon = tab.icon;
                        const label = t(tab.labelKey);
                        const isActive = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                aria-pressed={isActive}
                                aria-label={label}
                                className={`w-8 h-8 flex items-center justify-center rounded transition-colors border-0 bg-transparent cursor-pointer ${
                                    isActive ? "bg-white/20" : "hover:bg-white/10"
                                }`}
                                title={label}
                            >
                                <Icon style={{ color: "white", fontSize: 16 }} />
                            </button>
                        );
                    })}
                </div>
                <div className="flex items-center gap-1">
                    <Popover content={helpContent} placement="left" trigger="click" arrow={false}>
                        <button
                            type="button"
                            aria-label={t("parameter_picker.help.title")}
                            className="w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-white/10 rounded border-0 bg-transparent"
                        >
                            <InfoCircleOutlined style={{ color: "white", fontSize: 14 }} />
                        </button>
                    </Popover>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("parameter_picker.close")}
                        className="w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-white/10 rounded border-0 bg-transparent"
                    >
                        <CloseOutlined style={{ color: "white", fontSize: 14 }} />
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b border-gray-200">
                <Input
                    autoFocus
                    placeholder={t("parameter_picker.search_placeholder")}
                    value={search}
                    size="small"
                    onChange={({ target: { value } }) => onSearchChange(value)}
                    allowClear
                    prefix={<SearchOutlined className="text-gray-400" />}
                />
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-3 py-2" style={{ maxHeight: 400 }}>
                {filteredCategories.map((category) => (
                    <ParameterCategorySection
                        key={category.id}
                        category={category}
                        search={search}
                        expandedItems={expandedItems}
                        onToggleExpanded={toggleExpanded}
                        onSelect={onSelect}
                    />
                ))}
                {filteredCategories.filter((c) => {
                    if (!search) return true;
                    const filterItems = (items: ParameterItem[]): ParameterItem[] =>
                        items.filter(
                            (item) =>
                                item.name.toLowerCase().includes(search.toLowerCase()) ||
                                (item.children && filterItems(item.children).length > 0)
                        );
                    return filterItems(c.items).length > 0;
                }).length === 0 && (
                    <Typography.Text type="secondary" className="block text-center py-4">
                        {t("parameter_picker.no_parameters")}
                    </Typography.Text>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// Category Section Component
// ============================================================================

interface ParameterCategorySectionProps {
    category: ParameterCategory;
    search: string;
    expandedItems: Set<string>;
    onToggleExpanded: (id: string) => void;
    onSelect: (value: string, item: ParameterItem) => void;
}

const ParameterCategorySection = ({
    category,
    search,
    expandedItems,
    onToggleExpanded,
    onSelect,
}: ParameterCategorySectionProps) => {
    const isExpanded = expandedItems.has(category.id);
    const hasSearch = search.trim().length > 0;
    const shouldShowItems = isExpanded || hasSearch;

    // Filter items by search
    const filterItems = (items: ParameterItem[]): ParameterItem[] => {
        if (!search) return items;
        return items.filter((item) => {
            const matchesSelf = item.name.toLowerCase().includes(search.toLowerCase());
            const hasMatchingChildren = item.children && filterItems(item.children).length > 0;
            return matchesSelf || hasMatchingChildren;
        });
    };

    const filteredItems = filterItems(category.items);

    if (filteredItems.length === 0 && search) {
        return null;
    }

    return (
        <div className="mb-3">
            {/* Category header */}
            <button
                type="button"
                className="flex items-center gap-2 cursor-pointer py-1 hover:bg-gray-50 rounded w-full text-left border-0 bg-transparent"
                onClick={() => onToggleExpanded(category.id)}
                aria-expanded={shouldShowItems}
            >
                <span
                    className="text-xs text-gray-500 w-4 text-center"
                    style={{
                        transform: shouldShowItems ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                    }}
                >
                    ▶
                </span>
                {category.icon}
                <Typography.Text strong>{category.name}</Typography.Text>
                {category.count !== undefined && (
                    <Typography.Text type="secondary" className="text-xs">
                        {category.count}
                    </Typography.Text>
                )}
                {category.description && (
                    <Typography.Text type="secondary" className="text-xs ml-1">
                        {category.description}
                    </Typography.Text>
                )}
            </button>

            {/* Category items */}
            {shouldShowItems && (
                <div className="ml-4">
                    {filteredItems.map((item) => (
                        <ParameterItemComponent
                            key={item.id}
                            item={item}
                            categoryType={category.type}
                            search={search}
                            expandedItems={expandedItems}
                            onToggleExpanded={onToggleExpanded}
                            onSelect={onSelect}
                            depth={0}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Parameter Item Component
// ============================================================================

interface ParameterItemComponentProps {
    item: ParameterItem;
    categoryType: TabKey;
    search: string;
    expandedItems: Set<string>;
    onToggleExpanded: (id: string) => void;
    onSelect: (value: string, item: ParameterItem) => void;
    depth: number;
}

const ParameterItemComponent = ({
    item,
    categoryType,
    search,
    expandedItems,
    onToggleExpanded,
    onSelect,
    depth,
}: ParameterItemComponentProps) => {
    const { t } = usePaldockTranslation("components.connection-creator");
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const hasSearch = search.trim().length > 0;
    const shouldShowChildren = isExpanded || hasSearch;

    // Filter children by search
    const filterItems = (items: ParameterItem[]): ParameterItem[] => {
        if (!search) return items;
        return items.filter((child) => {
            const matchesSelf = child.name.toLowerCase().includes(search.toLowerCase());
            const hasMatchingChildren = child.children && filterItems(child.children).length > 0;
            return matchesSelf || hasMatchingChildren;
        });
    };

    const filteredChildren = hasChildren && item.children ? filterItems(item.children) : [];

    // Determine background color: item.color overrides category default (used for per-node-type coloring in steps)
    const paramTypeColor = item.paramType ? PARAM_TYPE_COLOR_MAP[item.paramType] : null;
    const defaultBgColor = PARAM_TYPE_COLOR_MAP[categoryType] ?? PARAMETER_COLORS.keys;
    const bgColor = item.color ?? paramTypeColor ?? defaultBgColor;

    const handleClick = () => {
        if (hasChildren) {
            onToggleExpanded(item.id);
        } else {
            const selectValue = item.value !== undefined ? item.value : `{${item.id}}`;
            onSelect(selectValue, item);
        }
    };

    const handleCopy = () => {
        const selectValue = item.value !== undefined ? item.value : `{${item.id}}`;
        const inner = selectValue.startsWith("{") && selectValue.endsWith("}") ? selectValue.slice(1, -1) : selectValue;
        const writeText = navigator.clipboard?.writeText?.bind(navigator.clipboard);
        if (!writeText) {
            toast.error(t("tag_input.copy_failed"), { autoClose: 2000 });
            return;
        }
        writeText(`{${inner}}`).then(
            () => toast.info(t("tag_input.copied_to_clipboard"), { autoClose: 2000 }),
            () => toast.error(t("tag_input.copy_failed"), { autoClose: 2000 })
        );
    };

    return (
        <div className="py-0.5" style={{ marginLeft: depth * 12 }}>
            <div className="group flex items-center gap-2">
                {hasChildren && (
                    <button
                        type="button"
                        className="text-xs text-gray-500 w-3 text-center cursor-pointer border-0 bg-transparent p-0"
                        style={{
                            transform: shouldShowChildren ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 0.2s",
                        }}
                        onClick={() => onToggleExpanded(item.id)}
                        aria-expanded={shouldShowChildren}
                        aria-label={shouldShowChildren ? t("parameter_picker.collapse") : t("parameter_picker.expand")}
                    >
                        ▶
                    </button>
                )}
                {!hasChildren && <span className="w-3" />}
                <button
                    type="button"
                    className="inline-flex items-center gap-1 cursor-pointer px-2 py-0.5 rounded hover:opacity-80 text-white text-sm border-0"
                    style={{ backgroundColor: bgColor }}
                    onClick={handleClick}
                    aria-expanded={hasChildren ? shouldShowChildren : undefined}
                >
                    {depth === 0 &&
                        item.nodeType &&
                        (() => {
                            const NodeIcon = getNodeEntry(item.nodeType as NodeType)?.icon;
                            return NodeIcon ? <NodeIcon className="w-3 h-3 flex-shrink-0" /> : null;
                        })()}
                    {item.name}
                </button>
                {!hasChildren && (
                    <button
                        type="button"
                        onClick={handleCopy}
                        aria-label={t("parameter_picker.copy_parameter")}
                        className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity border-0 bg-transparent p-0.5 cursor-pointer text-gray-400 hover:!text-gray-700"
                    >
                        <CopyOutlined style={{ fontSize: 12 }} />
                    </button>
                )}
            </div>

            {/* Render children */}
            {hasChildren && shouldShowChildren && (
                <div className="ml-3">
                    {filteredChildren.map((child) => (
                        <ParameterItemComponent
                            key={child.id}
                            item={child}
                            categoryType={categoryType}
                            search={search}
                            expandedItems={expandedItems}
                            onToggleExpanded={onToggleExpanded}
                            onSelect={onSelect}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Export compound component
// ============================================================================

export default ParameterPicker;
